import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useOrgStore } from '@/stores/orgStore'
import { useActor } from '@/lib/readiness/useReadiness'
import { useReadinessStore } from '@/stores/readinessStore'
import { notificationsFor, useNotificationStore, type Delivery } from '@/stores/notificationStore'

// App-header bell (Iteration 3 §13): personal notifications with read state.
// Unread dot on the icon; rows grouped Today · Earlier; unread rows tinted with
// a 6px dot; Mark all read. A click deep-links to the History entry. The
// Alerts module remains the platform-wide feed.

function ago(iso: string) {
  const min = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60_000))
  if (min < 60) return `${min || 1}m`
  const h = Math.floor(min / 60)
  if (h < 24) return `${h}h`
  return `${Math.floor(h / 24)}d`
}

export function NotificationBell() {
  const { currentOrgId: orgId } = useOrgStore()
  const user = useActor()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const all = useNotificationStore((s) => s.items)
  const delivery = useNotificationStore((s) => s.delivery[user] ?? 'immediate')
  const { markRead, markAllRead, setDelivery } = useNotificationStore.getState()
  const track = useReadinessStore((s) => s.track)
  const items = notificationsFor(all, orgId, user)
  const unread = items.filter((n) => !n.read_at).length
  const today = new Date().toDateString()
  const groups = [
    { label: 'Today', rows: items.filter((n) => new Date(n.created_at).toDateString() === today) },
    { label: 'Earlier', rows: items.filter((n) => new Date(n.created_at).toDateString() !== today) },
  ].filter((g) => g.rows.length)

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative p-2 rounded-lg text-subtle-light dark:text-subtle-dark hover:text-text-light dark:hover:text-text-dark hover:bg-primary/5 transition-colors"
        aria-label={unread ? `Notifications · ${unread} unread` : 'Notifications'}
        title="Notifications"
        data-testid="notification-bell"
      >
        <span className="material-icons text-xl">notifications</span>
        {unread > 0 && <span className="absolute top-1.5 right-1.5 h-2.5 w-2.5 rounded-full bg-error ring-2 ring-white dark:ring-neutral" data-testid="bell-unread-dot" />}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="rd absolute right-0 top-11 z-50 flex max-h-[70vh] w-[360px] max-w-[calc(100vw-24px)] flex-col rounded-2xl" style={{ background: 'var(--rd-bg-card)', boxShadow: 'var(--rd-shadow-float)', border: '1px solid var(--rd-border-subtle)' }} data-testid="notification-panel">
            <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: '1px solid var(--rd-border-subtle)' }}>
              <span className="text-sm font-semibold">Notifications{unread ? ` · ${unread} new` : ''}</span>
              <button type="button" className="rd-link text-xs" onClick={() => markAllRead(orgId, user)} disabled={!unread} data-testid="mark-all-read">Mark all read</button>
            </div>
            <div className="overflow-y-auto">
              {groups.length === 0 && <p className="px-4 py-8 text-center text-sm" style={{ color: 'var(--rd-text-muted)' }}>No notifications. Changes to objectives you own or follow appear here.</p>}
              {groups.map((g) => (
                <div key={g.label}>
                  <p className="rd-label px-4 pb-1 pt-3">{g.label}</p>
                  <ul>
                    {g.rows.map((n) => (
                      <li key={n.id}>
                        <button
                          type="button"
                          className="flex w-full items-start gap-2.5 px-4 py-2.5 text-left"
                          style={{ background: n.read_at ? undefined : 'color-mix(in srgb, var(--rd-accent-primary) 8%, transparent)' }}
                          onClick={() => {
                            markRead(n.id)
                            track(n.org_id, n.index_id, { name: 'readiness.notification_opened', kind: n.kind, objective: n.objective_code, event_id: n.event_id })
                            setOpen(false)
                            navigate(`/readiness/${n.objective_code}?index=${n.index_id}#ev_${n.event_id}`)
                          }}
                          data-testid={`notification-${n.id}`}
                        >
                          <span className="mt-1.5 inline-block h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: n.read_at ? 'transparent' : 'var(--rd-accent-primary)' }} aria-label={n.read_at ? undefined : 'unread'} />
                          <span className="min-w-0 flex-1">
                            <span className="block text-[14px] font-medium" style={{ color: 'var(--rd-text-primary)' }}>{n.consequence}</span>
                            <span className="block text-[12.5px]" style={{ color: 'var(--rd-text-muted)' }}>{n.cause}{n.raises_alert ? ' · alert raised' : ''}</span>
                          </span>
                          <span className="shrink-0 text-[11.5px]" style={{ color: 'var(--rd-text-faint)' }}>{ago(n.created_at)}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between gap-2 px-4 py-2.5 text-[12.5px]" style={{ borderTop: '1px solid var(--rd-border-subtle)', color: 'var(--rd-text-muted)' }}>
              <label className="flex items-center gap-1.5">
                Email
                <select aria-label="Email delivery" className="rd-input !w-auto !py-0.5 !text-[12.5px]" value={delivery} onChange={(e) => setDelivery(user, e.target.value as Delivery)}>
                  <option value="immediate">Immediate</option>
                  <option value="digest">Daily digest</option>
                </select>
              </label>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
